import { admitEvent } from './admission.js';
import { CommitClass, EventStatus, GovernanceDecisionValue, PromotionStatus, RegimeStatus } from './constants.js';
import { evaluateGovernance } from './governance.js';
import { buildPromotionRequest, advanceRegime } from './promotion.js';
import { appendTransition, validateTransition } from './transition-log.js';
import { createCanonicalRecord } from '../models/canonical-record.js';
import { createExperienceEvent } from '../models/event.js';
import { createEvidenceItem } from '../models/evidence.js';
import { createPromotionRequest } from '../models/promotion-request.js';
import { createQuarantineRecord } from '../models/quarantine-record.js';
import { deterministicHash, stableSerialize } from './stable.js';

const freeze = (value) => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};

export class ContractEngine {
  #state;
  #listeners = new Set();
  #promotionInputs = new Map();
  #pendingAdmissions = new Map();

  constructor({ scenarioId = 'scenario', stabilizationRequirement = 5, initialRegime = RegimeStatus.STABLE, stableSteps = 5 } = {}) {
    this.stabilizationRequirement = stabilizationRequirement;
    this.#state = freeze({
      scenarioId, events: [], traces: [], requests: [], decisions: [], canonicalRecords: [], transitionRecords: [], quarantineRecords: [],
      regime: { regimeId: `regime-${scenarioId}`, version: 1, status: initialRegime, stableSteps, stabilizationRequirement },
      timeline: [], rendererTransitions: [], quarantinedRequestIds: []
    });
  }

  getState() { return this.#state; }
  subscribe(listener) { this.#listeners.add(listener); return () => this.#listeners.delete(listener); }
  #replace(patch) { this.#state = freeze({ ...this.#state, ...patch }); }
  #emit(type, payload = {}) {
    const item = freeze({ sequence: this.#state.timeline.length + 1, type, ...payload });
    this.#replace({ timeline: [...this.#state.timeline, item], rendererTransitions: [...this.#state.rendererTransitions, item] });
    this.#listeners.forEach((listener) => listener(item));
    return item;
  }

  #evaluationBinding(trace, inputs = {}) {
    const predicateInputs = {
      evidence: trace.evidenceLedger,
      regime: this.#state.regime,
      stabilizationRequirement: this.stabilizationRequirement,
      directRecallAttempt: Boolean(inputs.directRecallAttempt),
      directReflexAttempt: Boolean(inputs.directReflexAttempt)
    };
    return freeze({
      traceVersion: trace.version,
      evaluatedEvidenceIds: trace.evidenceLedger.map((item) => item.evidenceId).sort(),
      predicateInputHash: deterministicHash(predicateInputs),
      regimeId: this.#state.regime.regimeId,
      regimeVersion: this.#state.regime.version,
      regimeStatus: this.#state.regime.status
    });
  }

  #assertFreshAuthorizationState(request) {
    const trace = this.#state.traces.find((item) => item.traceId === request.traceId);
    const inputs = this.#promotionInputs.get(request.requestId) || {};
    if (!trace) throw new Error('STALE_PROMOTION_AUTHORIZATION');
    const current = this.#evaluationBinding(trace, inputs);
    if (request.traceVersion !== current.traceVersion
      || stableSerialize(request.evaluatedEvidenceIds) !== stableSerialize(current.evaluatedEvidenceIds)
      || request.predicateInputHash !== current.predicateInputHash
      || request.regimeId !== current.regimeId
      || request.regimeVersion !== current.regimeVersion
      || request.regimeStatus !== current.regimeStatus) throw new Error('STALE_PROMOTION_AUTHORIZATION');
    return trace;
  }

  #authorizationHash(request, decision) {
    return deterministicHash({
      traceId: request.traceId,
      traceVersion: request.traceVersion,
      evaluatedEvidenceIds: request.evaluatedEvidenceIds,
      predicateInputHash: request.predicateInputHash,
      predicateVector: request.predicateResults,
      regimeId: request.regimeId,
      regimeVersion: request.regimeVersion,
      regimeStatus: request.regimeStatus,
      governanceDecision: decision.decision,
      governanceToken: decision.governanceToken,
      signer: decision.signer
    });
  }

  #assertGovernanceAuthorization(request, decision) {
    if (request.governanceDecision !== GovernanceDecisionValue.ALLOW
      || request.governanceToken !== decision.governanceToken
      || request.signer !== decision.signer
      || request.authorizationHash !== this.#authorizationHash(request, decision)) {
      throw new Error('STALE_PROMOTION_AUTHORIZATION');
    }
  }

  receiveEvent(event, evidenceItem = null) {
    const validatedEvent = createExperienceEvent(event);
    const validatedEvidence = evidenceItem ? createEvidenceItem(evidenceItem) : null;
    if (validatedEvidence && validatedEvidence.eventId !== validatedEvent.eventId) throw new Error('EVIDENCE_EVENT_ID_MISMATCH');
    if (validatedEvidence && validatedEvidence.sourceId !== validatedEvent.sourceId) throw new Error('EVIDENCE_SOURCE_MISMATCH');
    if (validatedEvidence && validatedEvidence.lineageId !== validatedEvent.lineageId) throw new Error('EVIDENCE_LINEAGE_MISMATCH');
    if (validatedEvidence && validatedEvidence.timestamp !== validatedEvent.timestamp) throw new Error('EVIDENCE_TIMESTAMP_MISMATCH');
    if (validatedEvidence && validatedEvidence.recallDerived !== validatedEvent.recallDerived) throw new Error('EVIDENCE_RECALL_ORIGIN_MISMATCH');
    if (validatedEvidence && validatedEvidence.reflexOriginated !== validatedEvent.reflexOriginated) throw new Error('EVIDENCE_REFLEX_ORIGIN_MISMATCH');
    if (validatedEvidence && validatedEvidence.provenanceComplete !== validatedEvent.provenance.complete) throw new Error('EVIDENCE_PROVENANCE_MISMATCH');
    const priorTrace = this.#state.traces[0] || null;
    if (priorTrace && priorTrace.representation !== validatedEvent.representation) throw new Error('EVENT_TRACE_REPRESENTATION_MISMATCH');
    if (this.#state.events.some((item) => item.eventId === validatedEvent.eventId)) throw new Error('EVENT_ID_DUPLICATE');
    const pending = freeze({ event: validatedEvent, evidenceItem: validatedEvidence, status: EventStatus.PENDING });
    this.#pendingAdmissions.set(validatedEvent.eventId, pending);
    this.#replace({ events: [...this.#state.events, validatedEvent] });
    this.#emit('EVENT_RECEIVED', { entityId: validatedEvent.eventId });
    return validatedEvent;
  }

  admitReceivedEvent(eventId) {
    const pending = this.#pendingAdmissions.get(eventId);
    if (!pending) throw new Error('PENDING_EVENT_NOT_FOUND');
    if (pending.status !== EventStatus.PENDING) throw new Error('EVENT_NOT_PENDING');
    if (pending.event.reflexOriginated && pending.event.originType === 'REFLEX_SIGNAL') {
      const rejected = freeze({ ...pending.event, status: EventStatus.REJECTED });
      this.#pendingAdmissions.delete(eventId);
      this.#replace({ events: this.#state.events.map((item) => item.eventId === eventId ? rejected : item) });
      const result = { admitted: false, event: rejected, reasonCode: 'REFLEX_REQUIRES_EVENT_ADMISSION' };
      this.#emit('EVENT_REJECTED', { entityId: eventId, reasonCodes: [result.reasonCode] });
      return result;
    }
    const admitted = freeze({ ...pending.event, status: EventStatus.ADMITTED });
    const updatedPending = freeze({ ...pending, event: admitted, status: EventStatus.ADMITTED });
    this.#pendingAdmissions.set(eventId, updatedPending);
    this.#replace({ events: this.#state.events.map((item) => item.eventId === eventId ? admitted : item) });
    this.#emit('EVENT_ADMITTED', { entityId: eventId });
    return { admitted: true, event: admitted };
  }

  associateAdmittedEvent(eventId) {
    const pending = this.#pendingAdmissions.get(eventId);
    if (!pending || pending.status !== EventStatus.ADMITTED) throw new Error('ADMITTED_EVENT_NOT_FOUND');
    const priorTrace = this.#state.traces[0] || null;
    const result = admitEvent(pending.event, priorTrace, pending.evidenceItem);
    if (!result.admitted) throw new Error('ASSOCIATIVE_ADMISSION_INVALID');
    this.#pendingAdmissions.delete(eventId);
    this.#replace({ events: this.#state.events.map((item) => item.eventId === eventId ? result.event : item), traces: [result.trace] });
    this.#emit(result.reasonCode, {
      entityId: result.trace.traceId,
      from: priorTrace ? CommitClass.ASSOCIATIVE : CommitClass.NONE,
      to: CommitClass.ASSOCIATIVE
    });
    return result;
  }

  admit(event, evidenceItem = null) {
    const received = this.receiveEvent(event, evidenceItem);
    const admission = this.admitReceivedEvent(received.eventId);
    if (!admission.admitted) return admission;
    return this.associateAdmittedEvent(received.eventId);
  }

  recall(traceId, timestamp) {
    const trace = this.#state.traces.find((item) => item.traceId === traceId);
    if (!trace) throw new Error('TRACE_NOT_FOUND');
    const updated = freeze({
      ...trace, version: trace.version + 1,
      attentionWeight: Math.min(1, trace.attentionWeight + 0.12), recallCount: trace.recallCount + 1, lastUpdatedAt: timestamp
    });
    this.#replace({ traces: this.#state.traces.map((item) => item.traceId === traceId ? updated : item) });
    this.#emit('ASSOCIATIVE_TRACE_RECALLED', { entityId: traceId, reasonCodes: ['ATTENTION_ONLY_RECALL'] });
    return updated;
  }

  setRegime(status) {
    if (!Object.values(RegimeStatus).includes(status)) throw new Error('REGIME_STATUS_INVALID');
    if (status === RegimeStatus.STABLE && this.#state.regime.status !== RegimeStatus.STABLE) throw new Error('REGIME_TRANSITION_BYPASS_FORBIDDEN');
    if (status === RegimeStatus.STABILIZING) return this.beginStabilization();
    const stableSteps = status === RegimeStatus.STABLE ? this.#state.regime.stableSteps : 0;
    this.#replace({ regime: {
      ...this.#state.regime, version: this.#state.regime.version + 1,
      status, stableSteps, stabilizationRequirement: this.stabilizationRequirement
    } });
    this.#emit('REGIME_CHANGED', { status });
    return this.#state.regime;
  }

  beginStabilization() {
    if (this.#state.regime.status !== RegimeStatus.UNSTABLE) throw new Error('STABILIZATION_REQUIRES_UNSTABLE_REGIME');
    const regime = freeze({
      ...this.#state.regime, version: this.#state.regime.version + 1,
      status: RegimeStatus.STABILIZING, stableSteps: 0, stabilizationRequirement: this.stabilizationRequirement
    });
    this.#replace({ regime });
    this.#emit('REGIME_CHANGED', { status: RegimeStatus.STABILIZING });
    return regime;
  }

  stabilizationStep() {
    const advanced = advanceRegime(this.#state.regime, this.stabilizationRequirement);
    const regime = freeze({ ...advanced, regimeId: this.#state.regime.regimeId, version: this.#state.regime.version + 1 });
    this.#replace({ regime });
    this.#emit('STABILIZATION_PROGRESS', { status: regime.status, stableSteps: regime.stableSteps, required: this.stabilizationRequirement });
    return regime;
  }

  requestPromotion({ requestId, requestedAt, requestedBy = 'scenario-runner', directRecallAttempt = false, directReflexAttempt = false, deferPredicates = false }) {
    const trace = this.#state.traces[0];
    if (!trace) throw new Error('TRACE_NOT_FOUND');
    if (this.#state.canonicalRecords.some((item) => item.sourceTraceId === trace.traceId)) throw new Error('TRACE_ALREADY_CANONICAL');
    if (this.#state.requests.some((item) => item.requestId === requestId)) throw new Error('REQUEST_ID_DUPLICATE');
    const inputs = { directRecallAttempt, directReflexAttempt };
    const binding = this.#evaluationBinding(trace, inputs);
    const pendingRequest = createPromotionRequest({
      requestId, traceId: trace.traceId, requestedAt, requestedBy,
      ...binding,
      evidenceDimensionIds: [...new Set(trace.evidenceLedger.map((item) => item.dimension))],
      provenanceStatus: trace.evidenceLedger.length > 0 && trace.evidenceLedger.every((item) => item.provenanceComplete) ? 'COMPLETE' : 'INCOMPLETE',
      regimeSnapshot: this.#state.regime,
      recallDerivedInputPresent: trace.evidenceLedger.some((item) => item.recallDerived),
      reflexOriginatedInputPresent: trace.evidenceLedger.some((item) => item.reflexOriginated),
      predicateResults: {}, governanceStatus: PromotionStatus.REQUESTED, quarantineStatus: 'CLEAR',
      governanceDecision: null, governanceToken: null, signer: null, authorizationHash: null
    });
    this.#promotionInputs.set(requestId, inputs);
    this.#replace({ requests: [...this.#state.requests, pendingRequest] });
    this.#emit('PROMOTION_REQUESTED', { entityId: requestId, traceId: trace.traceId });
    if (deferPredicates) return pendingRequest;
    return this.evaluatePromotion(requestId);
  }

  evaluatePromotion(requestId) {
    const pendingRequest = this.#state.requests.find((item) => item.requestId === requestId);
    if (!pendingRequest) throw new Error('REQUEST_NOT_FOUND');
    if (Object.keys(pendingRequest.predicateResults).length > 0) throw new Error('PREDICATES_ALREADY_EVALUATED');
    const trace = this.#state.traces.find((item) => item.traceId === pendingRequest.traceId);
    const inputs = this.#promotionInputs.get(requestId) || {};
    const evaluationBinding = this.#evaluationBinding(trace, inputs);
    const result = buildPromotionRequest({
      requestId, trace, requestedAt: pendingRequest.requestedAt, requestedBy: pendingRequest.requestedBy,
      regime: this.#state.regime, stabilizationRequirement: this.stabilizationRequirement, evaluationBinding, ...inputs
    });
    this.#replace({ requests: this.#state.requests.map((item) => item.requestId === requestId ? result.request : item) });
    this.#emit(result.predicatesSatisfied ? 'PREDICATES_SATISFIED' : 'PREDICATE_FAILED', { entityId: requestId, reasonCodes: result.reasonCodes });
    return result.request;
  }

  govern(requestId, options) {
    const request = this.#state.requests.find((item) => item.requestId === requestId);
    if (!request) throw new Error('REQUEST_NOT_FOUND');
    if (Object.keys(request.predicateResults).length === 0) throw new Error('PREDICATES_NOT_EVALUATED');
    const { deferCommit = false, ...governanceOptions } = options;
    const duplicate = this.#state.transitionRecords.find((item) => item.traceId === request.traceId && item.governanceToken === governanceOptions.governanceToken);
    if (duplicate) { this.#emit('REPLAY_DEDUPLICATED', { entityId: requestId, transitionRecordId: duplicate.transitionRecordId }); return this.#state.decisions.find((item) => item.requestId === requestId); }
    if (governanceOptions.requestedDecision === GovernanceDecisionValue.ALLOW) this.#assertFreshAuthorizationState(request);
    const decision = evaluateGovernance(request, governanceOptions);
    const status = decision.decision === GovernanceDecisionValue.ALLOW ? PromotionStatus.AUTHORIZED
      : decision.decision === GovernanceDecisionValue.QUARANTINE ? PromotionStatus.QUARANTINED
      : decision.decision === GovernanceDecisionValue.REVIEW ? PromotionStatus.UNDER_REVIEW : PromotionStatus.DENIED;
    const quarantineRecord = status === PromotionStatus.QUARANTINED
      ? createQuarantineRecord({
        quarantineId: `quarantine-${request.requestId}`,
        requestId: request.requestId,
        sourceTraceId: request.traceId,
        evidenceIds: this.#state.traces.find((item) => item.traceId === request.traceId).evidenceLedger.map((item) => item.evidenceId),
        reasonCodes: decision.reasonCodes.length ? decision.reasonCodes : ['GOVERNANCE_QUARANTINE'],
        governanceDecision: decision.decision,
        createdAt: decision.timestamp,
        status: 'ACTIVE'
      })
      : null;
    const authorized = decision.decision === GovernanceDecisionValue.ALLOW;
    const authorizationHash = authorized ? this.#authorizationHash(request, decision) : null;
    const updatedRequest = createPromotionRequest({
      ...request,
      governanceStatus: status,
      quarantineStatus: status === PromotionStatus.QUARANTINED ? 'QUARANTINED' : 'CLEAR',
      quarantineRecordId: quarantineRecord?.quarantineId || null,
      governanceDecision: decision.decision,
      governanceToken: decision.governanceToken,
      signer: decision.signer,
      authorizationHash
    });
    this.#replace({
      requests: this.#state.requests.map((item) => item.requestId === requestId ? updatedRequest : item),
      decisions: [...this.#state.decisions, decision],
      quarantineRecords: quarantineRecord && !this.#state.quarantineRecords.some((item) => item.requestId === requestId)
        ? [...this.#state.quarantineRecords, quarantineRecord]
        : this.#state.quarantineRecords,
      quarantinedRequestIds: status === PromotionStatus.QUARANTINED
        ? [...new Set([...this.#state.quarantinedRequestIds, requestId])]
        : this.#state.quarantinedRequestIds
    });
    this.#emit(`GOVERNANCE_${decision.decision}`, { entityId: decision.decisionId, requestId, reasonCodes: decision.reasonCodes });
    if (decision.decision === GovernanceDecisionValue.ALLOW && !deferCommit) this.#commit(updatedRequest, decision);
    return decision;
  }

  commitAuthorized(requestId) {
    const request = this.#state.requests.find((item) => item.requestId === requestId);
    const decision = this.#state.decisions.findLast((item) => item.requestId === requestId && item.decision === GovernanceDecisionValue.ALLOW);
    if (!request || request.governanceStatus !== PromotionStatus.AUTHORIZED || !decision) throw new Error('REQUEST_NOT_AUTHORIZED');
    this.#commit(request, decision);
    return this.#state.canonicalRecords.at(-1);
  }

  #commit(request, decision) {
    if (this.#state.canonicalRecords.some((item) => item.sourceTraceId === request.traceId)
      || this.#state.transitionRecords.some((item) => item.traceId === request.traceId)) throw new Error('TRACE_ALREADY_CANONICAL');
    const trace = this.#assertFreshAuthorizationState(request);
    this.#assertGovernanceAuthorization(request, decision);
    const transitionRecordId = `transition-${request.requestId}`;
    const log = appendTransition(this.#state.transitionRecords, {
      transitionRecordId, eventIds: trace.eventIds, traceId: trace.traceId,
      predicateVector: request.predicateResults, governanceDecision: decision.decision, governanceToken: decision.governanceToken,
      signer: decision.signer, timestamp: decision.timestamp
    });
    const transition = log.at(-1);
    const expectedPreviousHash = this.#state.transitionRecords.at(-1)?.recordHash || 'GENESIS';
    if (!validateTransition(transition, { expectedPreviousHash })) throw new Error('TRANSITION_RECORD_INVALID');
    const canonicalId = `canonical-${trace.traceId}`;
    if (this.#state.canonicalRecords.some((item) => item.canonicalId === canonicalId)) throw new Error('CANONICAL_ID_DUPLICATE');
    const canonical = createCanonicalRecord({
      canonicalId, sourceTraceId: trace.traceId, sourceEventIds: trace.eventIds,
      representation: trace.representation, predicateVector: request.predicateResults,
      governanceToken: decision.governanceToken, signer: decision.signer,
      provenanceLedger: trace.provenanceLedger, committedAt: decision.timestamp
    }, transition, { expectedPreviousHash });
    const committedRequest = freeze({ ...request, governanceStatus: PromotionStatus.COMMITTED });
    this.#replace({
      transitionRecords: log, canonicalRecords: [...this.#state.canonicalRecords, canonical],
      requests: this.#state.requests.map((item) => item.requestId === request.requestId ? committedRequest : item)
    });
    this.#emit('CANONICAL_COMMITTED', { entityId: canonical.canonicalId, from: CommitClass.ASSOCIATIVE, to: CommitClass.CANONICAL, transitionRecordId });
  }
}
